import { brandSpecific } from '../../utils/override';
import { TableBuilderColumnType } from './types';

/**
 * Column widths per breakpoint (CMS-1772 R1/R2). Breakpoints: 360 / md 768 /
 * lg 1280 / xl 1440.
 *
 * One model at every breakpoint: the identity column is a fixed track and the
 * data columns share the remaining container width equally, each holding its own
 * minimum. When even the minimums do not fit, the columns keep them and the
 * table scrolls with a sticky identity column instead of shrinking below them
 * (CMS-1521 / CMS-1590). Only the numbers differ by breakpoint — 360 and 768
 * take one minimum for every column type, 1280 and 1440 take a per-type one.
 *
 * The widths are grid tracks on the table rather than per-cell classes:
 * `minmax(min, 1fr)` is the only faithful expression of "share the remaining
 * width equally, but never below this minimum". A table's own layout algorithm
 * cannot clamp one column that way — fixed layout ignores cell min-widths and
 * splits the remainder evenly, which at 1280 (846px content width, identity +
 * reward + 4 attributes) would leave the reward column at 141px against its
 * 180px minimum.
 */
export const IDENTITY_COL_WIDTHS = {
  sm: '120px',
  md: '129px',
  lg: '141px',
  xl: '158px',
} as const;

/**
 * Identity column in Travel Insurance mode (CMS-1896 R7). Wider than the
 * general table at every breakpoint because TI CTA labels are much longer than
 * other verticals' and cannot be shortened.
 */
export const TI_IDENTITY_COL_WIDTHS = {
  sm: '150px',
  md: '178px',
  lg: '182px',
  xl: '182px',
} as const;

/**
 * Price column (CMS-1896 R7). A fixed track at every breakpoint, where the other
 * data columns are `minmax(min, 1fr)`: the content is system-generated with a
 * known worst case (a 7-digit price with thousands separator), so the column
 * takes no flex share and the remaining space flows to the attribute columns.
 */
export const PRICE_COL_WIDTHS = {
  sm: '133px',
  md: '163px',
  lg: '163px',
  xl: '163px',
} as const;

/**
 * Data-column minimum at 360 / 768 — identical for every column type. Amended by
 * Kimchi Yim on 2026-08-26: these were fixed widths, which left the table
 * narrower than its container whenever two or three columns fit inside it.
 */
export const DATA_COL_WIDTHS = {
  sm: '156px',
  md: '180px',
} as const;

/** Minimum a flexing data column holds at 1280 / 1440, by column type. */
export const DATA_COL_MIN_WIDTHS: Record<TableBuilderColumnType, string> = {
  HOT_REWARD_PICK: '180px',
  CATALOGUE_ATTRIBUTE: '120px',
  FREE_TEXT: '120px',
  // A regular attribute column's width (CMS-1922 R2): the pill needs no reserve.
  PL_TOTAL_REWARD: '120px',
};

/**
 * Cell padding shared by every header and body cell (CMS-1772 R4). Both tokens
 * are already redefined per breakpoint, so they must not carry a responsive
 * ladder: left/right resolve to 12/16/16/16px and top/bottom to 8/10/12/12px.
 *
 * The `y-` token on the horizontal axis is deliberate and comes from the spec,
 * which names `--spacing-padding-y-sm` for left/right here. It is not
 * interchangeable with `x-sm`: the two differ at 768, where `y-sm` is 16px and
 * `x-sm` is 12px, and 16px is the value R4 asks for. So this pairing has to be
 * revisited against the spec rather than "corrected" to the axis-matching
 * token — which is what this note exists to say.
 */
export const CELL_PADDING =
  'px-spacing/padding/y-sm py-spacing/padding/soft-12px';

/**
 * The identity column takes the narrower `soft-12px` on both axes instead
 * (left/right 12/16/16/16px → 8/10/12/12px), so the fixed identity track spends
 * less of its width on padding. Amended by Kimchi Yim on 2026-08-24 for the
 * identity cell; the column's header and category labels follow it so the
 * column keeps one left edge. Every other cell stays on `CELL_PADDING`.
 */
export const IDENTITY_CELL_PADDING = 'p-spacing/padding/soft-12px';

/**
 * Hot Reward Pick value-cell background (CMS-1772 R6).
 *
 * SingSaver reuses the brand tint (`#e0f4f8`) so the reward column reads as one
 * continuous band from header through cells. MoneyHero uses a lighter tint
 * (`#f3f5fb`) that has no design-token mapping, so it is a raw value here.
 * money101 and moneymax share SingSaver's palette, so the default covers them.
 */
export const HOT_REWARD_CELL_BG = brandSpecific({
  _: 'bg-fill/primary-light',
  moneyhero: 'bg-[#f3f5fb]',
});

/**
 * Attribute / identity header text colour (CMS-1772 R5) — `#424242`. Not
 * `text-text/secondary`: modulab's palette shadows that design token with
 * `#42424a`, so the token class renders a colour the spec does not ask for.
 */
export const NEUTRAL_HEADER_TEXT = 'text-colors/neutral-gray-800';

/**
 * Hot Reward Pick header text colour (CMS-1772 R5, as amended by Kimchi Yim on
 * 2026-08-11). Both brands use their own dark accent — SingSaver `#004a68`,
 * MoneyHero `#002981` — so this is one token, not a per-brand split. It also
 * completes R6's intent: with the header and body text sharing `text/accent`,
 * the SingSaver reward column reads as one continuous band top to bottom.
 */
export const HOT_REWARD_HEADER_TEXT = 'text-text/accent';
